import React, { useState, useRef, useCallback, useEffect } from "react";
import { X, Loader2, AlertCircle, Image, Info } from "lucide-react";
import { Toolbar } from "./markdownEditorPageToolbar";
import { CodeEditor } from "./markdownEditorPageCodeEditor";
import MarkdownRenderer from "./markdownRenderer/MarkdownRenderer";
import { Button } from "../../@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../@/components/ui/popover";

// ============= R2 CONFIGURATION =============
const R2_CONFIG = {
  BUCKET_NAME: import.meta.env.VITE_BUCKET_NAME,
  ACCESS_KEY: import.meta.env.VITE_R2_ACCESS_KEY,
  SECRET_KEY: import.meta.env.VITE_R2_SECRET_KEY,
  ENDPOINT: import.meta.env.VITE_R2_ENDPOINT_URL,
  PUBLIC_URL: import.meta.env.VITE_R2_PUBLIC_URL,
};

// ============= CRYPTO HELPERS =============
const bufferToHex = (buffer) => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const hmacSHA256 = async (key, data) => {
  const encoder = new TextEncoder();
  const keyData = typeof key === "string" ? encoder.encode(key) : key;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(data),
  );
  return new Uint8Array(signature);
};

const sha256Hex = async (data) => {
  const encoder = new TextEncoder();
  const dataToHash = typeof data === "string" ? encoder.encode(data) : data;
  const hash = await crypto.subtle.digest("SHA-256", dataToHash);
  return bufferToHex(hash);
};

const getSignatureKey = async (key, dateStamp, regionName, serviceName) => {
  const kDate = await hmacSHA256("AWS4" + key, dateStamp);
  const kRegion = await hmacSHA256(kDate, regionName);
  const kService = await hmacSHA256(kRegion, serviceName);
  return await hmacSHA256(kService, "aws4_request");
};

// === Markdown image helpers ===
const extractImageUrlsFromMarkdown = (text) => {
  if (!text) return [];
  const regex = /!\[[^\]]*\]\(([^)]+)\)/g;
  const urls = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    urls.push(match[1]);
  }
  return urls;
};

const derivePathFromUrl = (url) => {
  if (!url) return null;
  try {
    const publicBase = (R2_CONFIG.PUBLIC_URL || "").replace(/\/+$/g, "");
    if (publicBase && url.startsWith(publicBase)) {
      return url.slice(publicBase.length + 1).split("?")[0];
    }
    const bucket = R2_CONFIG.BUCKET_NAME || "";
    const bucketMarker = `/${bucket}/`;
    const idx = url.indexOf(bucketMarker);
    if (idx !== -1) {
      return url.slice(idx + bucketMarker.length).split("?")[0];
    }
  } catch (e) {
    // ignore
  }
  return null;
};

// ============= R2 UPLOAD FUNCTION =============
const uploadToR2 = async (file, contentFolder, contentName, imageOrder) => {
  try {
    console.log("Starting upload...", {
      file: file.name,
      contentFolder,
      contentName,
      imageOrder,
    });

    const fileExtension = file.name.split(".").pop().toLowerCase();
    // sanitize folder/name to avoid spaces and unsafe characters in object keys
    const slugify = (s) =>
      String(s || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9\-\_]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    const safeFolder = slugify(contentFolder);
    const safeName = slugify(contentName);
    const folderParts = safeFolder.split("-");
    const folderOrder = folderParts[0] || "1";

    // Path without leading bucket name for the object key
    const objectKey = `assets/images/${safeFolder}/${folderOrder}-${safeName}/${imageOrder}-image.${fileExtension}`;
    const fullPath = `/${R2_CONFIG.BUCKET_NAME}/${objectKey}`;

    console.log("Object key:", objectKey);
    console.log("Full path:", fullPath);

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    const fileBytes = new Uint8Array(arrayBuffer);

    // Calculate payload hash
    const payloadHash = await sha256Hex(fileBytes);
    console.log("Payload hash:", payloadHash);

    // Create date strings
    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.slice(0, 8);

    // AWS signature details
    const host = new URL(R2_CONFIG.ENDPOINT).host;
    const region = "auto";
    const service = "s3";
    const algorithm = "AWS4-HMAC-SHA256";

    // Create canonical request
    const canonicalUri = fullPath;
    const canonicalQuerystring = "";
    const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

    const canonicalRequest = [
      "PUT",
      canonicalUri,
      canonicalQuerystring,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    console.log("Canonical request:", canonicalRequest);

    // Create string to sign
    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = await sha256Hex(canonicalRequest);
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join("\n");

    console.log("String to sign:", stringToSign);

    // Calculate signature
    const signingKey = await getSignatureKey(
      R2_CONFIG.SECRET_KEY,
      dateStamp,
      region,
      service,
    );
    const signatureBytes = await crypto.subtle.sign(
      "HMAC",
      await crypto.subtle.importKey(
        "raw",
        signingKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      ),
      new TextEncoder().encode(stringToSign),
    );
    const signature = bufferToHex(signatureBytes);

    // Create authorization header
    const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    console.log("Authorization:", authorizationHeader);

    // Make the upload request
    const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;
    console.log("Upload URL:", url);

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        Host: host,
        Authorization: authorizationHeader,
        "x-amz-date": amzDate,
        "x-amz-content-sha256": payloadHash,
        "Content-Type": file.type || "image/jpeg",
        "Content-Length": fileBytes.length.toString(),
      },
      body: fileBytes,
    });

    console.log("Response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Upload failed:", errorText);
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    // Build a stable public URL. Append timestamp to bust caches when replacing images.
    const ts = Date.now();
    const publicBase = (R2_CONFIG.PUBLIC_URL || "").replace(/\/+$/g, "");
    const publicUrl = `${publicBase}/${objectKey}?t=${ts}`;
    console.log("Success! Public URL:", publicUrl);

    return {
      success: true,
      url: publicUrl,
      path: objectKey,
    };
  } catch (error) {
    console.error("R2 Upload Error:", error);
    return { success: false, error: error.message };
  }
};

const deleteFromR2 = async (path) => {
  try {
    console.log("Deleting from R2:", path);
    const fullPath = `/${R2_CONFIG.BUCKET_NAME}/${path}`;
    const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;

    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.slice(0, 8);

    const host = new URL(R2_CONFIG.ENDPOINT).host;
    const region = "auto";
    const service = "s3";
    const algorithm = "AWS4-HMAC-SHA256";

    const payloadHash = await sha256Hex("");
    const canonicalUri = fullPath;
    const canonicalQuerystring = "";
    const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

    const canonicalRequest = [
      "DELETE",
      canonicalUri,
      canonicalQuerystring,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = await sha256Hex(canonicalRequest);
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join("\n");

    const signingKey = await getSignatureKey(
      R2_CONFIG.SECRET_KEY,
      dateStamp,
      region,
      service,
    );
    const signatureBytes = await crypto.subtle.sign(
      "HMAC",
      await crypto.subtle.importKey(
        "raw",
        signingKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      ),
      new TextEncoder().encode(stringToSign),
    );
    const signature = bufferToHex(signatureBytes);

    const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Host: host,
        Authorization: authorizationHeader,
        "x-amz-date": amzDate,
        "x-amz-content-sha256": payloadHash,
      },
    });

    console.log("Delete response:", response.status);
    return response.ok;
  } catch (error) {
    console.error("R2 Delete Error:", error);
    return false;
  }
};

export default function MarkdownEditorDialog({
  open,
  initialValue = "",
  onClose,
  onSave,
  contentFolder = "1-lessons",
  contentName = "introduction",
}) {
  const [content, setContent] = useState(initialValue);
  const [viewMode, setViewMode] = useState("split");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageCounter, setImageCounter] = useState(1);
  const [uploadError, setUploadError] = useState(null);
  const textareaRef = useRef(null);
  const currentUploadRef = useRef(null);

  useEffect(() => {
    setContent(initialValue);
  }, [initialValue]);

  // When the dialog opens or the initial content changes, scan the content
  // for image markdown and populate `uploadedImages` so images are visible
  // and deletable even after reopening the editor.
  useEffect(() => {
    if (!open) return;
    const text = initialValue || "";
    const urls = extractImageUrlsFromMarkdown(text);
    if (urls.length === 0) return;

    const parsed = urls.map((u, idx) => ({
      url: u,
      path: derivePathFromUrl(u),
      order: idx + 1,
    }));

    setUploadedImages(parsed);
    setImageCounter(parsed.length + 1);
  }, [open, initialValue]);

  const handleImageUpload = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);

      if (files.length === 0) {
        return;
      }

      setIsUploading(true);
      setUploadError(null);

      const editor = textareaRef.current;

      try {
        let localCounter = imageCounter;
        for (const file of files) {
          const order = localCounter;
          // upload sequentially to preserve order and counter
          const result = await uploadToR2(
            file,
            contentFolder,
            contentName,
            order,
          );

          if (result.success) {
            setUploadedImages((prev) => [
              ...prev,
              { path: result.path, url: result.url, order },
            ]);

            const imageMarkdown = `![Image ${order}](${result.url})`;

            if (editor) {
              // Insert at current cursor position (do not replace selection)
              const pos = editor.getPosition();
              const range = new monaco.Range(
                pos.lineNumber,
                pos.column,
                pos.lineNumber,
                pos.column,
              );
              editor.executeEdits("image-upload", [
                { range, text: imageMarkdown + "\n" },
              ]);
              const newContent = editor.getValue();
              setContent(newContent);
              setTimeout(() => {
                editor.focus();
              }, 0);
            }

            localCounter += 1;
          } else {
            setUploadError(result.error);
          }
        }
        // update the shared state counter after uploads
        setImageCounter(localCounter);
      } catch (error) {
        setUploadError(error.message);
      } finally {
        setIsUploading(false);
        e.target.value = "";
      }
    },
    [contentFolder, contentName, imageCounter],
  );

  const handleDeleteImage = useCallback(async (img) => {
    // Optimistically remove from UI
    setUploadedImages((prev) => prev.filter((i) => i.path !== img.path));

    // Remove occurrences of the image markdown using the exact URL
    const editor = textareaRef.current;
    if (editor) {
      const value = editor.getValue();
      let newValue = value;

      // Prefer matching by object `path` (ignoring query params) because
      // public URLs include cache-busting query strings. Fall back to exact
      // URL match if no path is available.
      if (img.path) {
        const escapedPath = img.path.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
        const pathRegex = new RegExp(
          `!\\[[^\\]]*\\]\\([^\\)]*${escapedPath}[^\\)]*\\)\\n?`,
          "g",
        );
        newValue = value.replace(pathRegex, "");
      } else if (img.url) {
        const escaped = img.url.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
        const regex = new RegExp(`!\\[[^\\]]*\\](${escaped})\\n?`, "g");
        newValue = value.replace(regex, "");
      }

      // If nothing changed (no match), attempt a looser remove of any
      // markdown that contains the filename to handle slight variations.
      if (newValue === value && img.path) {
        const fileName = img.path.split("/").pop();
        if (fileName) {
          const escapedName = fileName.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
          const nameRegex = new RegExp(
            `!\\[[^\\]]*\\]\\([^\\)]*${escapedName}[^\\)]*\\)\\n?`,
            "g",
          );
          newValue = value.replace(nameRegex, "");
        }
      }

      if (newValue !== value) {
        editor.setValue(newValue);
        setContent(newValue);
      }
    }

    try {
      const ok = await deleteFromR2(img.path);
      if (!ok) {
        setUploadError("Failed to delete image from storage");
      }
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Failed to delete image");
    }
  }, []);

  const handleInsert = useCallback(
    (format) => {
      console.log("handleInsert called with:", format);
      console.log("Current content type:", typeof content);
      console.log("Current content:", content);

      const editor = textareaRef.current;
      if (!editor) {
        console.log("Editor not found");
        return;
      }

      const selection = editor.getSelection();
      const contentStr = String(content || "");
      const model = editor.getModel();
      const selectedText = model.getValueInRange(selection);

      console.log("Selected text:", selectedText);
      console.log("Format object:", format);

      // Handle legacy action format (for table)
      if (format && typeof format === "object" && format.action) {
        console.log("Using action format");
        editor.executeEdits("table-insert", [
          { range: selection, text: format.action },
        ]);
        const newText = editor.getValue();
        setContent(newText);
        editor.focus();
        setTimeout(() => {
          const newPosition = editor.getPosition();
          editor.setPosition(newPosition);
        }, 0);
        return;
      }

      // Extract format properties
      const before = format?.before || "";
      const after = format?.after || "";
      const placeholder = format?.placeholder || "";

      console.log(
        "Before:",
        before,
        "After:",
        after,
        "Placeholder:",
        placeholder,
      );

      let insertText;
      let cursorOffset;
      let newSelection;

      if (selectedText) {
        // If text is selected, wrap it with the syntax
        insertText = before + selectedText + after;
        newSelection = new monaco.Selection(
          selection.endLineNumber,
          selection.endColumn + before.length + after.length,
          selection.endLineNumber,
          selection.endColumn + before.length + after.length,
        );
      } else {
        // If no selection, insert placeholder text and select it
        insertText = before + placeholder + after;
        const startPosition = editor.getPosition();
        newSelection = new monaco.Selection(
          startPosition.lineNumber,
          startPosition.column + before.length,
          startPosition.lineNumber,
          startPosition.column + before.length + placeholder.length,
        );
      }

      console.log("Insert text:", insertText);

      editor.executeEdits("format-insert", [
        { range: selection, text: insertText },
      ]);
      const newText = editor.getValue();
      console.log("New text:", newText);
      setContent(newText);
      editor.focus();

      setTimeout(() => {
        editor.setSelection(newSelection);
      }, 0);
    },
    [content],
  );

  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent
        className="z-9999 border border-(--neutral-300) p-0 max-w-[90vw] h-[96vh] flex flex-col bg-(--surface-base) text-(--neutral-900) rounded-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-(--neutral-300)">
          <h2 className="text-lg font-bold text-(--neutral-900)">
            Markdown Editor
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-(--neutral-200)"
          >
            {/* <X className="w-5 h-5" /> */}
          </button>
        </div>
        {/* Toolbar */}
        <Toolbar
          onInsert={handleInsert}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onImageUpload={handleImageUpload}
          isUploading={isUploading}
        />
        {/* Upload Error */}
        {uploadError && (
          <div className="border-b border-(--color-primary) px-4 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-(--color-primary)" />
            <p className="text-sm text-(--color-primary)">
              Upload failed: {uploadError}
            </p>
            <button
              onClick={() => setUploadError(null)}
              className="ml-auto text-(--color-primary) hover:text-(--color-accent)"
            >
              ×
            </button>
          </div>
        )}
        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {(viewMode === "edit" || viewMode === "split") && (
            <div
              className={`${
                viewMode === "split" ? "w-1/2" : "w-full"
              } border-r border-(--neutral-300)`}
            >
              <div className="h-full">
                <CodeEditor
                  value={content}
                  onChange={(value) => setContent(value)}
                  placeholder="Start typing your markdown here..."
                  onInsert={handleInsert}
                  onMount={(editor) => (textareaRef.current = editor)}
                />
              </div>
            </div>
          )}
          {(viewMode === "preview" || viewMode === "split") && (
            <div
              className={`${
                viewMode === "split" ? "w-1/2" : "w-full"
              } overflow-y-auto`}
            >
              <div className="p-8 max-w-none prose dark:prose-invert">
                <MarkdownRenderer content={content} />
              </div>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-4 border-t border-(--neutral-300)">
          <div className="flex items-center gap-2">
            {uploadedImages.length > 0 && (
              <div className="flex items-center gap-2">
                {uploadedImages.map((img) => (
                  <div key={img.path} className="flex items-center gap-1">
                    <img
                      src={img.url}
                      alt={`uploaded-${img.order}`}
                      className="h-8 w-8 rounded object-cover border border-(--neutral-200)"
                    />
                    <button
                      title="Remove image"
                      onClick={() => handleDeleteImage(img)}
                      className="text-(--neutral-500) hover:text-(--color-destructive)"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-(--neutral-600)">
                  Uploaded {uploadedImages.length} image
                  {uploadedImages.length !== 1 ? "s" : ""} to R2
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => onSave(content)}>Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
